<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ResizableTextArea from 'shared/components/ResizableTextArea.vue';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';
import EmojiInput from 'shared/components/emoji/EmojiInput.vue';

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
  showAttachment: {
    type: Boolean,
    default: false,
  },
  showEmojiPicker: {
    type: Boolean,
    default: false,
  },
  chatInputText: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();

const isInputFocused = ref(false);
const userInput = ref('');
const showEmojiPickerPanel = ref(false);

watch(
  () => props.chatInputText,
  value => {
    userInput.value = value || '';
  },
  { immediate: true }
);

const showAttachmentButton = computed(
  () => props.showAttachment && !userInput.value.length
);

const showSendButton = computed(() => userInput.value.length > 0);

const placeholder = computed(
  () =>
    props.config.chatInputPlaceholder ||
    t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.CHAT_INPUT_PLACEHOLDER')
);

const toggleEmojiPicker = () => {
  showEmojiPickerPanel.value = !showEmojiPickerPanel.value;
};

const hideEmojiPicker = () => {
  showEmojiPickerPanel.value = false;
};

const emojiOnClick = emoji => {
  userInput.value = `${userInput.value}${emoji} `;
};
</script>

<template>
  <div class="relative w-full shrink-0 px-4 pb-1">
    <div
      class="relative flex h-10 items-center rounded-md bg-white dark:!bg-n-slate-3"
      :class="{ 'ring-2 ring-n-brand dark:ring-n-brand': isInputFocused }"
      @keydown.esc="hideEmojiPicker"
    >
      <ResizableTextArea
        id="chat-input"
        v-model="userInput"
        :rows="1"
        :placeholder="placeholder"
        class="!mb-0 h-8 min-h-8 max-h-8 flex-grow resize-none rounded-md border-0 border-none !bg-white px-3 pb-0 !pt-1.5 text-sm outline-none !outline-0 focus:outline-none dark:!bg-n-slate-3"
        @focus="isInputFocused = true"
        @blur="isInputFocused = false"
      />
      <div class="relative flex shrink-0 items-center gap-1 px-2">
        <button
          v-if="showAttachmentButton"
          type="button"
          class="flex min-h-8 min-w-8 items-center justify-center text-n-slate-12"
          aria-hidden="true"
          tabindex="-1"
        >
          <FluentIcon icon="attach" />
        </button>
        <button
          v-if="showEmojiPicker"
          type="button"
          class="flex min-h-8 min-w-8 items-center justify-center"
          :aria-label="$t('EMOJI.ARIA_LABEL')"
          @click="toggleEmojiPicker"
        >
          <FluentIcon
            icon="emoji"
            class="transition-all duration-150"
            :class="{
              'text-n-slate-12': !showEmojiPickerPanel,
              'text-n-brand': showEmojiPickerPanel,
            }"
          />
        </button>
        <EmojiInput
          v-if="showEmojiPicker && showEmojiPickerPanel"
          placement="above-toolbar-start"
          :on-click="emojiOnClick"
          @keydown.esc="hideEmojiPicker"
        />
        <button
          v-if="showSendButton"
          type="button"
          class="flex min-h-8 min-w-8 items-center justify-center text-n-slate-12"
          aria-hidden="true"
          tabindex="-1"
        >
          <FluentIcon class="icon-send" icon="send" />
        </button>
      </div>
    </div>
  </div>
</template>
